import { lazy, Suspense } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import "./App.css";

const Admin = lazy(() => import("./pages/Admin/Admin"));

const BodyWeight = lazy(() => import("./pages/Dashboard/BodyWeight/BodyWeight"));

const ExerciseManagement = lazy(() => import("./pages/Admin/ExerciseManagement/ExerciseManagement"));
const ExerciseStats = lazy(() => import("./pages/Workout/components/ExerciseStats/ExerciseStats"));

const OrganizationList = lazy(() =>
  import("./pages/Admin/Organizations/OrganizationList").then((module) => ({ default: module.OrganizationList })),
);
const OrganizationDetail = lazy(() =>
  import("./pages/Admin/Organizations/OrganizationDetail").then((module) => ({ default: module.OrganizationDetail })),
);
const OrganizationNew = lazy(() =>
  import("./pages/Admin/Organizations/OrganizationNew").then((module) => ({ default: module.OrganizationNew })),
);
const PlansHome = lazy(() =>
  import("./pages/Admin/Plans/PlansHome/PlansHome").then((module) => ({ default: module.PlansHome })),
);
const SubscriptionsHome = lazy(() =>
  import("./pages/Admin/Subscriptions/SubscriptionsHome/SubscriptionsHome").then((module) => ({
    default: module.SubscriptionsHome,
  })),
);
const SubscriptionDetail = lazy(() =>
  import("./pages/Admin/Subscriptions/SubscriptionDetail/SubscriptionDetail").then((module) => ({
    default: module.SubscriptionDetail,
  })),
);

const Events = lazy(() => import("./pages/Dashboard/Events/EventsView"));

const Profile = lazy(() => import("./pages/Dashboard/Profile/Profile"));

const ProgramDayEdit = lazy(() => import("./pages/Program/ProgramDayEdit/ProgramDayEdit"));
const ProgramDetail = lazy(() => import("./pages/Program/ProgramDetail/ProgramDetail"));
const ProgramNew = lazy(() => import("./pages/Program/ProgramNew/ProgramNew"));
const ProgramList = lazy(() => import("./pages/Program/ProgramList/ProgramList"));
const ProgramDayOptimize = lazy(() => import("./pages/Program/ProgramDayOptimize/ProgramDayOptimize"));

const Settings = lazy(() => import("./pages/Dashboard/Settings/Settings"));

const UserManagement = lazy(() => import("./pages/Admin/UserManagement/UserManagement"));

const CompactWorkoutPage = lazy(() => import("./pages/Workout/CompactWorkout/CompactWorkoutPage"));
const WorkoutDetail = lazy(() => import("./pages/Workout/WorkoutDetail/WorkoutDetail"));
const WorkoutEdit = lazy(() => import("./pages/Workout/WorkoutEdit/WorkoutEdit"));
const WorkoutList = lazy(() => import("./pages/Workout/WorkoutList/WorkoutList"));
const WorkoutLive = lazy(() => import("./pages/Workout/WorkoutLive/WorkoutLive"));
const WorkoutNew = lazy(() => import("./pages/Workout/WorkoutNew/WorkoutNew"));
const WorkoutSummary = lazy(() => import("./pages/Workout/WorkoutSummary/WorkoutSummary"));
const WorkoutsPage = lazy(() => import("./pages/Workout/WorkoutsPage/WorkoutsPage"));

const NutritionCalculator = lazy(() => import("./pages/Clients/NutritionCalculator/NutritionCalculator"));

import { Navigation } from "./components/ui/Navigation/Navigation";
import { PageSuspense } from "./components/ui/PageSuspense/PageSuspense";
import ProtectedRoutes from "./components/providers/ProtectedRoutes/ProtectedRoutes";
import { SuperAdminRoute } from "./components/providers/SuperAdminRoute/SuperAdminRoute";
import { UserProvider } from "./components/providers/UserContext/UserContext";
import { HintBalloonProvider } from "./components/ui/HintBalloon/HintBalloon";
import { Toaster } from "react-hot-toast";
import CookieBanner from "./components/CookieBanner/CookieBanner";

const Goals = lazy(() => import('./pages/Goals/Goals'));
const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard").then((module) => ({ default: module.Dashboard })));
const Home = lazy(() => import("./pages/Home/Home"));
const Login = lazy(() => import("./pages/Login/Login").then((module) => ({ default: module.Login })));
const PlanEdit = lazy(() =>
  import("./pages/Admin/Plans/PlanEdit/PlanEdit").then((module) => ({ default: module.PlanEdit })),
);
const PlanView = lazy(() =>
  import("./pages/Admin/Plans/PlanView/PlanView").then((module) => ({ default: module.PlanView })),
);

function App() {
  const location = useLocation();

  return (
    <UserProvider>
      <HintBalloonProvider key={location.pathname} screenKey={location.pathname}>
        <Toaster position="top-right" />
        <CookieBanner />
        <div className="">{["/login"].includes(location.pathname) || location.pathname.startsWith("/workout/live/") ? null : <Navigation />}</div>
        {/* Safety-net Suspense — per-route PageSuspense boundaries handle normal cases */}
        <Suspense fallback={null}>
          <Routes>
          <Route element={<ProtectedRoutes />}>
            <Route path="/" element={<PageSuspense><Home /></PageSuspense>} />
            <Route path="programs" element={<PageSuspense><ProgramList /></PageSuspense>} />
            <Route path="program/:id" element={<PageSuspense><ProgramDetail /></PageSuspense>} />
            <Route path="program/new/" element={<PageSuspense><ProgramNew /></PageSuspense>} />
            <Route path="program/new/:id" element={<PageSuspense><ProgramNew /></PageSuspense>} />
            <Route path="/program/:programId/day/:id" element={<PageSuspense><ProgramDayEdit /></PageSuspense>} />
            <Route path="/program/:programId/day/:dayId/optimize" element={<PageSuspense><ProgramDayOptimize /></PageSuspense>} />
            <Route path="workouts" element={<PageSuspense><WorkoutsPage /></PageSuspense>} />
            <Route path="workout" element={<PageSuspense><WorkoutList /></PageSuspense>} />
            <Route path="workout/:id" element={<PageSuspense><WorkoutDetail /></PageSuspense>} />
            <Route path="workout/compact/:id" element={<PageSuspense><CompactWorkoutPage /></PageSuspense>} />
            <Route path="workout/live/:id" element={<PageSuspense><WorkoutLive /></PageSuspense>} />
            <Route path="workout/new" element={<PageSuspense><WorkoutNew /></PageSuspense>} />
            <Route path="workout/new/:id" element={<PageSuspense><WorkoutEdit /></PageSuspense>} />
            <Route path="workout/:id/summary" element={<PageSuspense><WorkoutSummary /></PageSuspense>} />
            <Route path="/stats/:personId?/:exerciseId?" element={<PageSuspense><ExerciseStats /></PageSuspense>} />
            <Route path="/goals" element={<PageSuspense><Goals /></PageSuspense>} />
            <Route path="/dashboard" element={<PageSuspense><Dashboard /></PageSuspense>} />
            <Route path="/dashboard/bodyweight" element={<PageSuspense><BodyWeight /></PageSuspense>} />
            <Route path="/dashboard/profile" element={<PageSuspense><Profile /></PageSuspense>} />
            <Route path="/dashboard/settings" element={<PageSuspense><Settings /></PageSuspense>} />
            <Route path="/admin" element={<PageSuspense><Admin /></PageSuspense>} />
            <Route path="/admin/exercisemanagement" element={<PageSuspense><ExerciseManagement /></PageSuspense>} />
            <Route path="/admin/plans" element={<PageSuspense><PlansHome /></PageSuspense>} />
            <Route path="/admin/plans/:id" element={<PageSuspense><PlanView /></PageSuspense>} />
            <Route path="/admin/plans/:id/edit" element={<PageSuspense><PlanEdit /></PageSuspense>} />
            <Route path="/admin/plans/new" element={<PageSuspense><PlanEdit /></PageSuspense>} />
            <Route path="/admin/usermanagement" element={<PageSuspense><UserManagement /></PageSuspense>} />
            <Route path="/clients/:clientId/nutrition" element={<PageSuspense><NutritionCalculator /></PageSuspense>} />
            <Route path="/admin/subscriptions" element={<PageSuspense><SubscriptionsHome /></PageSuspense>} />
            <Route path="/admin/subscriptions/:id" element={<PageSuspense><SubscriptionDetail /></PageSuspense>} />

            {/* Super Admin Only Routes */}
            <Route
              path="/admin/organizations"
              element={
                <PageSuspense>
                  <SuperAdminRoute>
                    <OrganizationList />
                  </SuperAdminRoute>
                </PageSuspense>
              }
            />
            <Route
              path="/admin/organizations/new"
              element={
                <PageSuspense>
                  <SuperAdminRoute>
                    <OrganizationNew />
                  </SuperAdminRoute>
                </PageSuspense>
              }
            />
            <Route
              path="/admin/organizations/:id"
              element={
                <PageSuspense>
                  <SuperAdminRoute>
                    <OrganizationDetail />
                  </SuperAdminRoute>
                </PageSuspense>
              }
            />
          </Route>
          <Route path="/login" element={<PageSuspense><Login /></PageSuspense>} />
          <Route path="/events" element={<PageSuspense><Events /></PageSuspense>} />
          </Routes>
        </Suspense>
      </HintBalloonProvider>
    </UserProvider>
  );
}

export default App;
